import React, { Component } from 'react';

import './field.css';

import _get from 'lodash.get';

import { FieldAPI } from '@philips-emr/tws-components-api';
import { FieldVisibility } from '@philips-emr/tws-components-api/lib/constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { any, arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string, validators } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { validate } from '@philips-emr/tws-core/lib/services/model-service';
import { REQUIRED } from '@philips-emr/tws-model/lib/validators/required';
import { classNames, enumValues, getLostProps, isFunction, isUndefined, isValidWidth, propagateProps, shallowCompare } from '@philips-emr/tws-utils';

import { ValidationList } from '..';

const { VIEWING, EDITING, BOTH } = FieldVisibility;

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.DATA_ENTRY,
];

@AwesomeComponent({ type: ComponentType.FORM, api: FieldAPI })
class Field extends Component {

  static propTypes = {
    alwaysShowLabel: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: any,
    editing: bool.notEnumerable(),
    entry: string,
    hideLabel: bool,
    label: expression,
    localValidators: object.notEnumerable(),
    mdWidth: number.group('size'),
    onBlur: eventListener,
    onChange: eventListener,
    onKeyUp: eventListener,
    onValidate: eventListener,
    smWidth: number.group('size'),
    uuid: string,
    forceValidate: bool.notEnumerable(),
    validatedOnChange: bool.group('validation'),
    validators: validators,
    visibility: oneOf(enumValues(FieldVisibility)),
    visible: bool,
    width: number.group('size'),
    xsmWidth: number.group('size'),
  };

  static defaultProps = {
    alwaysShowLabel: false,
    data: {},
    hideLabel: false,
    label: 'Sample label',
    localValidators: {},
    validatedOnChange: true,
    validators: [],
    visibility: BOTH,
    visible: true,
    width: 3,
  };

  state = {
    validations: [],
    childValidations: [],
    showValidations: false,
  };

  componentDidMount() {
    const { editing } = this.props;

    editing && this.validate(this.getValue(), false);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { entry, data } = this.props;

    // validators should preferably be out of this list, but its contents may change the result of isRequired
    if (shallowCompare(this.props, nextProps, [
      'alwaysShowLabel',
      'children',
      'className',
      'editing',
      'entry',
      'forceValidate',
      'hideLabel',
      'label',
      'mdWidth',
      'smWidth',
      'uuid',
      'validators',
      'visibility',
      'visible',
      'width',
      'xsmWidth',
    ])) {
      return true;
    }

    if (shallowCompare(this.state, nextState, ['showValidations', 'valid', 'validations'])) {
      return true;
    }

    if (entry) {
      if (_get(data, entry) !== _get(nextProps.data, entry)) {
        return true;
      }
    } else {
      if (data !== nextProps.data) {
        return true;
      }
    }

    return false;
  }

  componentDidUpdate(oldProps) {
    const { editing: oldEditing, forceValidate: oldForceValidate } = oldProps;
    const { editing, forceValidate } = this.props;

    if ((!oldEditing && editing) || (!oldForceValidate && forceValidate)) {
      this.validate(this.getValue(), forceValidate);
    }

    if (oldEditing && !editing) {
      this.resetValidations(true);
    }
  }

  getClassName() {
    const { className, smWidth, mdWidth, xsmWidth, width } = this.props;

    return classNames({
      'c-field': true,
      [className]: className,
      [`u-col-${width}`]: isValidWidth(width),
      [`u-col-${xsmWidth}@xsm`]: isValidWidth(xsmWidth),
      [`u-col-${smWidth}@sm`]: isValidWidth(smWidth),
      [`u-col-${mdWidth}@md`]: isValidWidth(mdWidth),
    });
  }

  isFieldVisible() {
    const { editing, visibility, visible } = this.props;

    if (!visible) {
      return false;
    }

    return (
      visibility === BOTH
      || (visibility === VIEWING && !editing)
      || (visibility === EDITING && editing)
    );
  }

  onChange = ({ value, oldValue, validations: childValidations }) => {
    const { onChange, entry, validatedOnChange } = this.props;

    const callback = () => {
      if (validatedOnChange) {
        this.validate(value, true);
      }

      if (isFunction(onChange)) {
        onChange({ entry, value, oldValue });
      }
    }

    if (childValidations) {
      this.setState({ childValidations }, callback);
    } else {
      callback();
    }
  };

  onBlur = (...args) => {
    const { onBlur } = this.props;

    this.validate(this.getValue(), true);

    isFunction(onBlur) && onBlur(...args);
  };

  onKeyUp = (...args) => {
    const { onKeyUp, validatedOnChange } = this.props;
    const { showValidations } = this.state;

    if (showValidations && !validatedOnChange) {
      this.resetValidations();
    }

    isFunction(onKeyUp) && onKeyUp(...args);
  };

  getValue() {
    const { data, entry } = this.props;
    return _get(data, entry);
  }

  resetValidations(resetChild = false) {
    this.setState(({ childValidations }) => ({
      childValidations: resetChild ? [] : childValidations,
      validations: [],
      showValidations: false,
    }));
  }

  runValidations(value) {
    const { validators } = this.props;

    const validationRunner = validate(value);

    validators.forEach(validator => (
      validationRunner.apply(validator.name, validator.options)
    ));

    return validationRunner.run();
  }

  validate(value = this.getValue(), showValidations = false) {
    const { entry, localValidators, onValidate } = this.props;
    const { childValidations } = this.state;

    const localValidations = Object.values(localValidators).flatMap(validator => validator(value));

    const standardValidations = this.runValidations(value);
    const validations = [...localValidations, ...standardValidations, ...childValidations];
    const valid = !validations.find(v => !v.valid);

    this.setState({ validations, valid, showValidations }, () => {
      isFunction(onValidate) && onValidate({ entry, validations, valid });
    });

    return validations;
  }

  renderChildren() {
    const { alwaysShowLabel, children, data, editing, entry, label } = this.props;
    const { showValidations, valid } = this.state;

    const lostProps = getLostProps(Field.propTypes, this.props);

    const readOnly = editing === false;

    const inherited = {
      ...lostProps,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onKeyUp: this.onKeyUp,
      placeholder: !readOnly && !alwaysShowLabel ? label : '',
      readOnly,
      valid: valid || !showValidations || !editing,
    };

    const property = entry ? 'value' : 'data';
    const value = entry ? this.getValue() : data;

    const props = {
      ...inherited,
      [property]: value,
    };

    return propagateProps(children, props);
  }

  isRequired() {
    const { validators } = this.props;

    return validators.some(validator => validator.name === REQUIRED);
  }

  renderLabel() {
    const { label, editing, hideLabel, alwaysShowLabel } = this.props;
    const value = this.getValue();

    const className = classNames({
      'c-label': true,
      'u-hide': hideLabel,
      'u-hidden': editing && (isUndefined(value) || value === '' || value === false) && !alwaysShowLabel && !this.isRequired(),
      'is-required': this.isRequired(),
    });

    return <label className={className}>{msg(label)}</label>;
  }

  render() {
    if (!this.isFieldVisible()) return null;

    const { uuid, editing } = this.props;
    const { validations, showValidations } = this.state;

    const Container = ContainerProvider.provide(this);

    return (
      <div className={this.getClassName()} data-testid="field">
        {this.renderLabel()}
        <Container className="c-field__content" acceptedTypes={acceptedTypes} uuid={uuid} >
          {this.renderChildren()}
        </Container>

        {editing && showValidations &&
        <ValidationList data={validations} />}
      </div>
    );
  }
}

export default Field;
